import Layout from '@/components/Layout'
import { useState, useEffect, useMemo } from 'react'
import { getClientWalletStatement } from '@/services/api'
import api from '@/services/api'
import { API_ENDPOINTS } from '@/config/api'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { formatAmount } from '@/utils/constants'
import '../reports/style.css'

/**
 * ClientsAccountStatement Component
 * 
 * Page for viewing clients account statements with filters and transaction details.
 */
function ClientsAccountStatement() {
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 7)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })
  const [toDate, setToDate] = useState(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })
  const [userSearch, setUserSearch] = useState('')
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)
  const [recommendations, setRecommendations] = useState([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await getClientWalletStatement({
        fromDate,
        toDate,
        username: userSearch || undefined
      })
      if (response && response.success && response.data) {
        const mappedData = response.data.map((item, index) => {
          const dateObj = new Date(item.date)
          return {
            id: index,
            date: dateObj.toLocaleDateString('en-CA'),
            time: dateObj.toTimeString().split(' ')[0],
            description: item.description,
            detail: item.source, // Using source as detail based on user request/sample
            pl: item.pnl,
            creditLimit: item.credit_limit || '-',
            balance: parseFloat(item.balance)
          }
        })
        setTableData(mappedData)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  // Debounced User Search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (userSearch.length >= 3) {
        fetchUserSuggestions(userSearch)
      } else {
        setRecommendations([])
        setShowDropdown(false)
      }
    }, 0)
    return () => clearTimeout(timer)
  }, [userSearch])

  const fetchUserSuggestions = async (searchTerm) => {
    setSearchLoading(true)
    setShowDropdown(true)
    setRecommendations([])
    try {
      const response = await api.get(API_ENDPOINTS.SEARCH_BY_USERNAME, {
        params: { username: searchTerm }
      })
      if (response.data?.data?.results && Array.isArray(response.data.data.results)) {
        setRecommendations(response.data.data.results)
      } else {
        setRecommendations([])
      }
    } catch (error) {
      console.error('Error fetching user suggestions:', error)
      setRecommendations([])
    } finally {
      setSearchLoading(false)
    }
  }

  const handleSelectUser = (username) => {
    setUserSearch(username)
    setShowDropdown(false)
    setRecommendations([])
  }

  // Group data by date
  const groupedData = useMemo(() => {
    const grouped = {}
    tableData.forEach(item => {
      if (!grouped[item.date]) {
        grouped[item.date] = []
      }
      grouped[item.date].push(item)
    })
    // Sort dates in descending order
    return Object.keys(grouped)
      .sort((a, b) => new Date(b) - new Date(a))
      .map(date => ({
        date,
        items: grouped[date].sort((a, b) => {
          // Sort by time in descending order
          return b.time.localeCompare(a.time)
        })
      }))
  }, [tableData])



  // Handle Search button click
  const handleSearch = () => {
    fetchData()
  }

  // Format date for display (e.g., "21/12/2025")
  const formatDisplayDate = (dateString) => {
    const date = new Date(dateString)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  return (
    <Layout>
      <div className="p-4 w-full mx-auto bg-[#171b36] min-h-screen">
        {/* Header Section */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 pt-4"
        >
          <div className="flex justify-between items-start mb-2">
            <div
              style={{ color: "#cae0e8", fontSize: "24px" }}
              className="font-semibold"
            >
              Clients Account Statement
            </div>
          </div>
          <div className="w-full h-px bg-white mb-2"></div>
        </div>

        {/* Filter Section */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", marginTop: "-10px" }}
          className="shadow-sm px-4 py-2"
        >
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div className="flex flex-col">
              <label className="text-white text-[12px] font-bold mb-1">From</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-white text-[12px] font-bold mb-1">To</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
              />
            </div>

            <div className="flex flex-col relative">
              <label className="text-white text-[12px] font-bold mb-1">Search by user</label>
              <input
                type="text"
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value)
                  setShowDropdown(false)
                }}
                placeholder="Enter Atleast 3 character"
                className="px-2 py-2 text-[11px] bg-white text-black placeholder-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
              />
              {showDropdown && (
                <ul className="absolute top-full left-0 w-full bg-white text-black text-[12px] rounded-sm shadow-lg max-h-40 overflow-y-auto z-50">
                  {searchLoading ? (
                    <li className="px-2 py-2 text-gray-500 italic">Searching...</li>
                  ) : recommendations.length > 0 ? (
                    recommendations.map((user, index) => (
                      <li
                        key={index}
                        onClick={() => handleSelectUser(user.username)}
                        className="px-2 py-2 cursor-pointer hover:bg-blue-100"
                      >
                        {user.username}
                      </li>
                    ))
                  ) : (
                    <li className="px-2 py-2 text-gray-500 italic">No record found</li>
                  )}
                </ul>
              )}
            </div>

            <button
              onClick={handleSearch}
              disabled={loading}
              className="px-4 py-2 bg-[#333d79] text-white font-medium text-[12px] rounded-sm hover:bg-[#2d3569] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faSearch} className="h-3 w-3" />
              {loading ? 'Loading...' : 'Search'}
            </button>
          </div>
        </div>

        {/* Table Section */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 py-2"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Date
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Description
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    P&L
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Credit Limit
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-2 py-8 text-center text-gray-400">
                      Loading...
                    </td>
                  </tr>
                ) : groupedData.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-2 py-8 text-center text-gray-400">
                      There are no records to show
                    </td>
                  </tr>
                ) : (
                  groupedData.map((group, groupIndex) =>
                    group.items.map((row, rowIndex) => (
                      <tr
                        key={row.id}
                        className={`transition-colors ${(groupIndex + rowIndex) % 2 === 0 ? "bg-[rgba(245,245,245,0.1)]" : ""
                          }`}
                      >
                        <td className="py-1 px-2 text-white text-[13px]">
                          {rowIndex === 0 ? (
                            <div>
                              <div>{formatDisplayDate(row.date)}</div>
                              <div className="text-[11px] text-gray-400">{row.time}</div>
                            </div>
                          ) : (
                            <div className="text-[11px] text-gray-400">{row.time}</div>
                          )}
                        </td>
                        <td className="py-1 px-2 text-white text-[13px]">
                          <div>
                            <div className="font-medium">{row.description}</div>
                            <div className="text-[11px] text-gray-300">{row.detail}</div>
                          </div>
                        </td>
                        <td className="py-1 px-2 text-[13px] font-bold">
                          <span
                            className={row.pl >= 0 ? 'text-[#3cd247]' : 'text-[#f43]'}
                          >
                            {formatAmount(row.pl)}
                          </span>
                        </td>
                        <td className="py-1 px-2 text-white text-[13px]">
                          {row.creditLimit === '-' ? '-' : formatAmount(row.creditLimit)}
                        </td>
                        <td className="py-1 px-2 text-white text-[13px]">
                          {formatAmount(row.balance)}
                        </td>
                      </tr>
                    ))
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default ClientsAccountStatement

